import { Modal } from "tailwindcss-stimulus-components";

// Node detail modal for the READONLY glance-view graph (runbooks#show,
// card #2338). Modelled exactly on mission_task_tracking_modal_controller.js
// -- a <dialog> + lazy turbo_frame_tag, reset-and-reload on each open so a
// second node click always shows fresh content rather than the previous
// node's cached frame. Attached alongside (not instead of) the existing
// runbook-graph controller on the same #runbook_canvas element -- see
// runbooks/_graph.html.erb.
//
// Chevron navigation (card #2921) -- same interaction primitive as
// response_modal_controller.js's prev()/next() (index math + flashChevron +
// keyboard arrows), sourced differently: response_modal scans the DOM for
// currently-visible rows because a response TABLE can be live-filtered.
// There's no equivalent concern here -- a node's position on the canvas is
// arbitrary/dragged, not the run's walk order -- so the navigable node-id
// list is rendered ONCE, server-side, as a data value on #runbook_canvas
// (nodeIdsValue, sourced from run.run_nodes.order(:id) -- see
// runbooks/_graph.html.erb's `walked_node_ids`) rather than scanned from the
// DOM on every navigation. Only present when a specific Runbook::Run is in
// context (runIdValue) -- the Definition show page's plain glance view has
// no run, so nodeIdsValue is empty and the chevrons stay hidden throughout.
export default class RunbookNodeDetailModal extends Modal {
  static targets = ["dialog", "frame", "prevButton", "nextButton"];
  static values = { runId: String, nodeIds: Array, autoOpenNodeId: String };

  connect() {
    super.connect();
    this.currentNodeIndex = -1;
    this.handleKeyDownBound = this.handleKeyDown.bind(this);
    this.handleEscapeBound = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        this.close(event);
      }
    };
    if (this.hasDialogTarget) {
      this.dialogTarget.addEventListener("keydown", this.handleKeyDownBound);
      this.dialogTarget.addEventListener("keydown", this.handleEscapeBound);
    }

    // Deep link (?node=<id> on the graph page): the server only sets this
    // value for a node belonging to this definition, so it goes straight
    // through the same open path a node click uses.
    if (this.hasAutoOpenNodeIdValue && this.autoOpenNodeIdValue) {
      this.openNode(this.autoOpenNodeIdValue);
    }
  }

  disconnect() {
    if (this.hasDialogTarget) {
      this.dialogTarget.removeEventListener("keydown", this.handleKeyDownBound);
      this.dialogTarget.removeEventListener("keydown", this.handleEscapeBound);
    }
    super.disconnect();
  }

  open(event) {
    // Node cards are keyboard-activatable too (role="button" + tabindex="0"
    // in _graph.html.erb, Vela review card #2338 -- a click-only <div>
    // action is otherwise mouse-only). Space's default behaviour is to
    // scroll the page, so a keydown-triggered open needs its own
    // preventDefault -- a plain click never reaches here with a key event.
    if (event?.type === "keydown") {
      event.preventDefault();
    }

    // Extract the node id from the clicked node card BEFORE opening the
    // dialog (same order as the mission-task modal's getTaskIdFromEvent).
    this.openNode(this.getNodeIdFromEvent(event), event);
  }

  openNode(nodeId, event) {
    if (this.hasFrameTarget) {
      this.resetFrame();
    }

    super.open(event);

    this.currentNodeIndex = this.nodeIdsValue.indexOf(Number(nodeId));

    if (nodeId) {
      this.loadContent(nodeId);
    }

    this.updateNavButtons();

    if (this.hasDialogTarget) {
      this.dialogTarget.focus();
    }
  }

  getNodeIdFromEvent(event) {
    if (!event?.target) return null;
    const nodeCard = event.target.closest("[data-runbook-node-detail-modal-node-id-value]");
    return nodeCard ? nodeCard.dataset.runbookNodeDetailModalNodeIdValue : null;
  }

  close(event) {
    event?.preventDefault();
    super.close(event);
  }

  resetFrame() {
    this.frameTarget.removeAttribute("src");
    this.frameTarget.removeAttribute("complete");
    this.frameTarget.removeAttribute("loading");
    this.frameTarget.innerHTML = this.loadingSpinnerHTML();
  }

  loadingSpinnerHTML() {
    return `<div class="flex-1 flex items-center justify-center">
      <div class="flex flex-col items-center">
        <svg class="animate-spin h-8 w-8 text-blue-600 mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <p class="text-sm text-gray-500">Loading node details...</p>
      </div>
    </div>`;
  }

  loadContent(nodeId) {
    if (this.hasFrameTarget && nodeId) {
      // Carries the in-context run through on every reload (Runbook::
      // NodesController#show's run_id param, card #2921) so the modal's
      // "Latest run" panel always reflects THIS run, not whichever run
      // happens to be globally most recent.
      const url = this.runIdValue
        ? `/admin/nodes/${nodeId}?run_id=${this.runIdValue}`
        : `/admin/nodes/${nodeId}`;
      this.frameTarget.setAttribute("src", url);
    }
  }

  navigateToNode(index) {
    if (index < 0 || index >= this.nodeIdsValue.length) return;

    this.currentNodeIndex = index;
    this.loadContent(this.nodeIdsValue[index]);
    this.updateNavButtons();
  }

  flashChevron(direction) {
    let btn = null;
    if (direction === "left" && this.hasPrevButtonTarget) {
      btn = this.prevButtonTarget;
    } else if (direction === "right" && this.hasNextButtonTarget) {
      btn = this.nextButtonTarget;
    }
    if (btn) {
      // Save original classes and content
      const originalClasses = btn.getAttribute("data-original-classes") || btn.className;
      const originalContent = btn.innerHTML;
      btn.setAttribute("data-original-classes", originalClasses);
      btn.setAttribute("data-original-content", originalContent);

      // Replace content with spinner
      btn.innerHTML = `
        <svg class="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      `;

      // Add loading state classes
      btn.classList.remove("bg-white/80", "text-gray-700", "hover:bg-white");
      btn.classList.add("bg-sapphire", "text-white");

      // Restore original state after turbo frame loads
      const restoreButton = () => {
        btn.className = btn.getAttribute("data-original-classes");
        btn.innerHTML = btn.getAttribute("data-original-content");
        btn.removeAttribute("data-original-content");
        this.frameTarget.removeEventListener("turbo:frame-load", restoreButton);
      };

      // Listen for frame load to restore button
      if (this.hasFrameTarget) {
        this.frameTarget.addEventListener("turbo:frame-load", restoreButton);
      }

      // Force a reflow to ensure the class changes are applied
      void btn.offsetWidth;
    }
  }

  prev() {
    if (this.currentNodeIndex <= 0) return;

    this.flashChevron("left");
    this.navigateToNode(this.currentNodeIndex - 1);
  }

  next() {
    if (this.currentNodeIndex === -1 || this.currentNodeIndex >= this.nodeIdsValue.length - 1) return;

    this.flashChevron("right");
    this.navigateToNode(this.currentNodeIndex + 1);
  }

  handleKeyDown(event) {
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      this.prev();
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      this.next();
    }
  }

  updateNavButtons() {
    if (this.hasPrevButtonTarget) {
      const canGoPrev = this.currentNodeIndex > 0;
      this.prevButtonTarget.classList.toggle("hidden", !canGoPrev);
      this.prevButtonTarget.disabled = !canGoPrev;
    }

    if (this.hasNextButtonTarget) {
      const canGoNext = this.currentNodeIndex !== -1 && this.currentNodeIndex < this.nodeIdsValue.length - 1;
      this.nextButtonTarget.classList.toggle("hidden", !canGoNext);
      this.nextButtonTarget.disabled = !canGoNext;
    }
  }
}
